import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import {
  getAdminPartners, createPartner, updatePartner, deletePartner,
  updatePartnerVisibility, isPartnerApiUnavailable,
  PARTNER_TYPES, PARTNER_TYPE_LABEL, partnerTypes,
} from '../../../api/partnerApi';
import { uploadFile } from '../../../api/fileApi';
import { extractApiErrorMessage } from '../../../api/adminApi';
import { downscaleImage, RESIZE_PRESETS } from '../../../shared/utils/imageResize';
import PartnerLogo from '../../../shared/components/PartnerLogo';

const EDITABLE_TYPES = PARTNER_TYPES.filter(t => t.key !== 'ALL');

/**
 * 서버로 보내는 기본 정보 필드.
 * 폼 상태에는 미리보기용 logoUrl이, 수정 시에는 서버가 준 createdAt·published까지
 * 섞여 있으므로 그대로 되보내지 않고 이 목록만 골라 담는다.
 */
const PAYLOAD_FIELDS = [
  'types', 'name', 'slug', 'tagline', 'region', 'logoFileId',
  'homepage', 'email', 'instagram', 'youtube', 'description',
];

/** 빈 문자열은 '값 없음'으로 보내 서버에 ''가 저장되지 않게 한다 */
const toPayload = (form) => {
  const out = {};
  PAYLOAD_FIELDS.forEach(k => {
    const v = form[k];
    if (Array.isArray(v)) { out[k] = v; return; }
    out[k] = typeof v === 'string' && v.trim() === '' ? null : v ?? null;
  });
  return out;
};

/** 서버가 사유를 보냈으면 그대로, 아니면 상태 코드라도 알린다 */
const failureText = (err, fallback) => {
  const code = err.response?.status;
  const detail = extractApiErrorMessage(err);
  if (detail) return `${detail}${code ? ` (HTTP ${code})` : ''}`;
  return `${fallback} (HTTP ${code ?? '네트워크'})`;
};

const EMPTY = {
  // 제작사이면서 소속사인 업체가 있어 여러 개를 고를 수 있다
  types: ['PRODUCTION'], name: '', slug: '', tagline: '', region: '',
  // 서버는 logoFileId로 저장하고 조회 시 logoUrl로 돌려준다.
  // 폼에서는 미리보기용 logoUrl과 전송용 logoFileId를 함께 들고 다닌다.
  logoFileId: null, logoUrl: '',
  homepage: '', email: '', instagram: '', youtube: '', description: '',
};

function PartnerForm({ initial, onSubmit, onCancel, saving }) {
  const [form, setForm] = useState(initial ?? EMPTY);
  const [uploading, setUploading] = useState(false);
  useEffect(() => { setForm(initial ?? EMPTY); }, [initial]);

  const set = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }));

  // 저장된 파트너를 다시 열면 types가 없고 type만 있을 수 있다
  const selectedTypes = partnerTypes(form);

  /** 마지막 하나는 끌 수 없다 — 종류가 없으면 상세에 그릴 섹션이 사라진다 */
  const toggleType = (key) => setForm(prev => {
    const now = partnerTypes(prev);
    const next = now.includes(key) ? now.filter(t => t !== key) : [...now, key];
    return next.length === 0 ? prev : { ...prev, types: next, type: next[0] };
  });
  const canSubmit = form.name.trim() && selectedTypes.length > 0 && !saving && !uploading;

  /**
   * @param {{ wide?: boolean, multiline?: boolean }} [opts]
   *   multiline — 줄바꿈을 넣을 수 있는 칸. 단일 입력에서는 엔터가 저장으로 새어나간다.
   */
  const field = (label, key, placeholder, opts = {}) => {
    const { wide = false, multiline = false } = opts;
    return (
      <div style={{ flex: wide ? '1 1 100%' : '1 1 220px', minWidth: 200 }}>
        <label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-tertiary)', marginBottom: 6 }}>
          {label}
        </label>
        {multiline ? (
          <textarea
            className="ag-field__input"
            value={form[key] ?? ''}
            onChange={set(key)}
            placeholder={placeholder}
            rows={2}
            style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit' }}
          />
        ) : (
          <input
            className="ag-field__input"
            value={form[key] ?? ''}
            onChange={set(key)}
            placeholder={placeholder}
            style={{ width: '100%' }}
          />
        )}
      </div>
    );
  };

  return (
    <form
      className="ag-card"
      style={{ marginBottom: 12 }}
      onSubmit={(e) => {
        e.preventDefault();
        if (!canSubmit) return;
        // 서버는 logoFileId만 받는다 — logoUrl은 화면 미리보기 전용
        const { logoUrl, ...payload } = form;
        onSubmit(payload);
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
        {initial?.id ? '파트너 수정' : '파트너 등록'}
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 180px', minWidth: 160 }}>
          <label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-tertiary)', marginBottom: 6 }}>
            종류 *
          </label>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', padding: '8px 0' }}>
            {EDITABLE_TYPES.map(t => (
              <label key={t.key} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={selectedTypes.includes(t.key)}
                  onChange={() => toggleType(t.key)}
                />
                {t.label}
              </label>
            ))}
          </div>
        </div>
        {field('이름 *', 'name', '예: 스튜디오 모먼트')}
        {field('slug (URL)', 'slug', 'studio-moment')}
        {field('한 줄 소개', 'tagline', '영화 · 웹드라마\n엔터로 줄을 바꿀 수 있습니다', { multiline: true })}
        {field('지역', 'region', '서울')}
        <div style={{ flex: '1 1 100%' }}>
          <label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-tertiary)', marginBottom: 6 }}>
            로고
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 72, height: 72, borderRadius: 10, flexShrink: 0, overflow: 'hidden',
              border: '1px solid var(--color-border)', background: 'var(--color-background)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {form.logoUrl
                ? <PartnerLogo src={form.logoUrl} alt="로고 미리보기" background="transparent" />
                : <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>없음</span>}
            </div>
            <label className="ag-btn ag-btn--sm" style={{ cursor: uploading ? 'default' : 'pointer' }}>
              <i className="ti ti-upload" /> {uploading ? '업로드 중...' : form.logoUrl ? '로고 변경' : '로고 첨부'}
              <input
                type="file" accept="image/*" style={{ display: 'none' }} disabled={uploading}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (!file) return;
                  setUploading(true);
                  try {
                    // 원본을 그대로 올리면 72~148px로 줄여 쓰면서 매번 원본을 내려받게 된다
                    const sized = await downscaleImage(file, RESIZE_PRESETS.logo);
                    const { fileEntityId, url } = await uploadFile(sized, 'IMAGE');
                    setForm(prev => ({ ...prev, logoFileId: fileEntityId, logoUrl: url }));
                  } catch (err) {
                    window.alert(err.message || '로고 업로드에 실패했습니다.');
                  } finally {
                    setUploading(false);
                  }
                }}
              />
            </label>
            {form.logoUrl && (
              <button
                type="button"
                className="ag-btn ag-btn--sm"
                onClick={() => setForm(prev => ({ ...prev, logoFileId: null, logoUrl: '' }))}
              >
                제거
              </button>
            )}
          </div>
          <p style={{ fontSize: 11, color: 'var(--color-text-tertiary)', margin: '8px 0 0', lineHeight: 1.6 }}>
            <b>정사각형 1:1</b> 권장 · 512×512px 이상<br />
            <b>배경은 흰색</b>으로 저장해주세요. 투명 PNG는 리사이즈 과정에서 배경이 검게 깔립니다.<br />
            가로로 긴 로고도 잘리지 않고 그대로 들어갑니다. 올리면 512px로 자동 축소됩니다.
          </p>
        </div>
        {field('홈페이지', 'homepage', 'studiomoment.example.com')}
        {field('이메일', 'email', 'contact@example.com')}
        {field('인스타그램', 'instagram', '@studio_moment')}
        {field('유튜브', 'youtube', 'youtube.com/@studiomoment')}
      </div>

      <div style={{ marginTop: 12 }}>
        <label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-tertiary)', marginBottom: 6 }}>
          소개
        </label>
        <textarea
          className="ag-field__input"
          value={form.description ?? ''}
          onChange={set('description')}
          rows={4}
          placeholder="파트너 소개를 입력하세요."
          style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit' }}
        />
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button className="ag-btn ag-btn--primary" type="submit" disabled={!canSubmit}>
          <i className="ti ti-device-floppy" /> {saving ? '저장 중...' : '저장'}
        </button>
        {onCancel && <button className="ag-btn" type="button" onClick={onCancel}>취소</button>}
      </div>

      <p style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginTop: 10 }}>
        필모그래피·소속 배우·학과·할인 혜택·갤러리는 저장 후 상세에서 관리합니다.
      </p>
    </form>
  );
}

export default function AdminPartnersPage() {
  const navigate = useNavigate();
  const [type, setType] = useState('ALL');
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unavailable, setUnavailable] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const reqIdRef = useRef(0);

  const fetchPartners = useCallback(async (t_) => {
    const myReq = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const { partners: list } = await getAdminPartners({ type: t_, limit: 100 });
      if (myReq !== reqIdRef.current) return;
      setPartners(list);
      setUnavailable(false);
    } catch (err) {
      if (myReq !== reqIdRef.current) return;
      if (isPartnerApiUnavailable(err)) { setUnavailable(true); setPartners([]); return; }
      const code = err.response?.status;
      const detail = extractApiErrorMessage(err);
      setError(detail
        ? `목록을 불러오지 못했습니다 — ${detail}${code ? ` (HTTP ${code})` : ''}`
        : (code ? `목록을 불러오지 못했습니다. (HTTP ${code})` : (err.message || '목록을 불러오지 못했습니다.')));
      setPartners([]);
    } finally {
      if (myReq === reqIdRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPartners(type); }, [type, fetchPartners]);

  const handleSubmit = async (form) => {
    setSaving(true);
    setError(null);
    try {
      const payload = toPayload(form);
      if (form.id) await updatePartner(form.id, { id: form.id, ...payload });
      else await createPartner(payload);
      setEditing(null);
      fetchPartners(type);
    } catch (err) {
      setError(failureText(err, '저장에 실패했습니다.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`'${p.name}' 파트너를 삭제할까요?\n되돌릴 수 없습니다.`)) return;
    setBusyId(p.id);
    try {
      await deletePartner(p.id);
      fetchPartners(type);
    } catch (err) {
      setError(failureText(err, '삭제에 실패했습니다.'));
    } finally {
      setBusyId(null);
    }
  };

  const handleVisibility = async (p) => {
    const next = !p.published;
    setBusyId(p.id);
    setPartners(prev => prev.map(x => (x.id === p.id ? { ...x, published: next } : x)));
    try {
      await updatePartnerVisibility(p.id, next);
    } catch (err) {
      setPartners(prev => prev.map(x => (x.id === p.id ? { ...x, published: p.published } : x)));
      setError(failureText(err, '노출 설정에 실패했습니다.'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <AdminLayout pageId="partners">
      <div className="admin-topbar">
        <h1>파트너 관리</h1>
        <div className="admin-topbar__right">
          <span style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>총 {partners.length}개</span>
          <span className="admin-badge"><i className="ti ti-user" /> admin</span>
        </div>
      </div>

      <PartnerForm
        key={editing?.id ?? 'new'}
        initial={editing}
        saving={saving}
        onSubmit={handleSubmit}
        onCancel={editing ? () => setEditing(null) : undefined}
      />

      <div className="ag-filter-row">
        <span style={{ fontSize: 12, color: 'var(--color-text-tertiary)', flexShrink: 0 }}>종류:</span>
        {PARTNER_TYPES.map(f => (
          <button
            key={f.key}
            className={`ag-filter-chip${type === f.key ? ' active' : ''}`}
            onClick={() => setType(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {error && (
        <div style={{ fontSize: 12, color: 'var(--color-danger)', marginBottom: 10 }}>
          <i className="ti ti-alert-triangle" /> {error}
        </div>
      )}

      {unavailable && (
        <div className="ag-card" style={{ marginBottom: 12, fontSize: 13 }}>
          <i className="ti ti-info-circle" /> 파트너 API가 아직 배포되지 않았습니다.
        </div>
      )}

      <div className="ag-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="ag-table">
            <thead>
              <tr>{['이름', '종류', '지역', 'slug', '노출', '관리'].map(h => <th key={h}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="ag-table__empty">불러오는 중...</td></tr>
              ) : partners.length === 0 ? (
                <tr><td colSpan={6} className="ag-table__empty">등록된 파트너가 없습니다</td></tr>
              ) : partners.map(p => (
                <tr key={p.id}>
                  <td style={{ fontSize: 13, fontWeight: 500 }}>{p.name}</td>
                  <td style={{ color: 'var(--color-text-secondary)' }}>
                    {partnerTypes(p).map(t => PARTNER_TYPE_LABEL[t]).join(' · ')}
                  </td>
                  <td style={{ color: 'var(--color-text-secondary)' }}>{p.region ?? '—'}</td>
                  <td style={{ color: 'var(--color-text-secondary)' }}>{p.slug ?? '—'}</td>
                  <td>
                    <button
                      className={`ag-badge ${p.published === false ? 'ag-badge--gray' : 'ag-badge--success'}`}
                      style={{ border: 'none', cursor: 'pointer' }}
                      disabled={busyId === p.id}
                      onClick={() => handleVisibility(p)}
                    >
                      {p.published === false ? '숨김' : '노출'}
                    </button>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="ag-btn ag-btn--sm" onClick={() => setEditing(p)}>
                        <i className="ti ti-edit" /> 기본정보
                      </button>
                      <button className="ag-btn ag-btn--sm" onClick={() => navigate(`/admin/partners/${p.id}`)}>
                        <i className="ti ti-list-details" /> 상세
                      </button>
                      <button
                        className="ag-btn ag-btn--sm ag-btn--danger"
                        disabled={busyId === p.id}
                        onClick={() => handleDelete(p)}
                      >
                        삭제
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
